import { cn } from "lib/utils";
import { CheckCircle2 } from "lucide-react";
import {
  type FeatureEmailMock,
  type FeatureMatchingMock,
  type FeatureProposalMock,
  lpConfig,
} from "routes/LandingPage/lpConfig";
import { useFadeIn } from "routes/LandingPage/useFadeIn";

/**
 * メール解析のモックUIを生成する
 * @return {JSX.Element} メール解析のモックUI
 */
function MockEmailParsing() {
  const mock = lpConfig.features.items[0].mockData as FeatureEmailMock;
  return (
    <div className="space-y-4">
      {/* Email header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="w-8 h-8 rounded-full bg-[#1667CF]/20" />
        <div className="space-y-1.5 flex-1">
          <div className="bg-gray-200 rounded h-3 w-40" />
          <div className="bg-gray-100 rounded h-2.5 w-56" />
        </div>
      </div>
      {/* Parsed fields */}
      <div className="space-y-2.5">
        {mock.parsedFields.map(field => (
          <div key={field.label} className="flex items-center gap-3">
            <span className="text-xs font-bold text-[#1667CF] bg-[#1667CF]/10 px-2 py-1 rounded w-20 text-center shrink-0">
              {field.label}
            </span>
            <span className="text-sm text-gray-700">{field.value}</span>
          </div>
        ))}
      </div>
      {/* Status indicator */}
      {mock.piiLabel && (
        <div className="mt-4 flex items-center gap-2 bg-green-50 rounded-lg px-3 py-2">
          <CheckCircle2 className="w-4 h-4 text-[#30BF78]" />
          <span className="text-xs text-[#30BF78] font-medium">
            {mock.piiLabel}
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * マッチングスコアのモックUIを生成する
 * @return {JSX.Element} マッチングスコアのモックUI
 */
function MockMatchingScore() {
  const mock = lpConfig.features.items[1].mockData as FeatureMatchingMock;
  return (
    <div className="space-y-4">
      {/* Score header */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-bold text-[#0F1649]">
          {mock.resultLabel}
        </span>
        <span className="text-xs text-gray-400">{mock.hitCount}</span>
      </div>
      {/* Score bars */}
      {mock.scores.map(item => (
        <div key={item.label} className="space-y-1.5">
          <div className="flex justify-between text-xs">
            <span className="text-gray-600">{item.label}</span>
            <span className="font-bold text-[#0F1649]">{item.score}%</span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className={cn(
                "h-full rounded-full",
                item.score >= 95
                  ? "bg-[#30BF78]"
                  : item.score >= 90
                    ? "bg-[#2B92FF]"
                    : "bg-[#1667CF]",
              )}
              style={{ width: `${item.score}%` }}
            />
          </div>
        </div>
      ))}
      {/* Depth filter */}
      <div className="mt-3 flex items-center gap-2 bg-gray-100 rounded-lg px-3 py-2">
        <span className="text-xs text-gray-500">商流フィルタ:</span>
        <span className="text-xs font-bold text-[#0F1649]">
          {mock.depthFilter}
        </span>
      </div>
    </div>
  );
}

/**
 * 提案メールのモックUIを生成する
 * @return {JSX.Element} 提案メールのモックUI
 */
function MockProposalEmail() {
  const mock = lpConfig.features.items[2].mockData as FeatureProposalMock;
  return (
    <div className="space-y-4">
      {/* Email compose header */}
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-400 w-12 shrink-0">To:</span>
          <div className="bg-gray-100 rounded h-7 flex-1 flex items-center px-3">
            <span className="text-xs text-gray-500">{mock.to}</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-400 w-12 shrink-0">件名:</span>
          <div className="bg-gray-100 rounded h-7 flex-1 flex items-center px-3">
            <span className="text-xs text-gray-500">{mock.subject}</span>
          </div>
        </div>
      </div>
      {/* Email body skeleton */}
      <div className="border-t border-gray-100 pt-3 space-y-2">
        <div className="bg-gray-100 rounded h-3 w-full" />
        <div className="bg-gray-100 rounded h-3 w-5/6" />
        <div className="bg-gray-100 rounded h-3 w-4/5" />
        <div className="bg-gray-50 rounded h-3 w-full" />
        <div className="bg-gray-50 rounded h-3 w-3/4" />
      </div>
      {/* Send button */}
      <div className="pt-2">
        <div className="inline-flex items-center gap-2 bg-[#ff8d00] text-white text-xs font-bold px-4 py-2 rounded-lg">
          {mock.sendButton}
        </div>
      </div>
    </div>
  );
}

const MOCK_COMPONENTS = [
  <MockEmailParsing key="email" />,
  <MockMatchingScore key="matching" />,
  <MockProposalEmail key="proposal" />,
];

/**
 * ランディングページの機能詳細セクション
 * 3つの主要機能をジグザグレイアウトとモックUIで視覚的に紹介する
 * @return {JSX.Element} 機能詳細セクションコンポーネント
 */
export function LpFeatures() {
  const fade = useFadeIn();

  const features = lpConfig.features.items.map((item, index) => ({
    title: item.title,
    items: item.bullets,
    mockContent: MOCK_COMPONENTS[index],
  }));

  return (
    <section id="features" className="py-24 lg:py-32 bg-white">
      <div
        ref={fade.ref}
        className={cn(
          "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 lp-fade-in-up",
          fade.isVisible && "lp-visible",
        )}
      >
        <div className="text-center mb-16">
          <span className="text-xs font-bold tracking-widest text-[#ff8d00] uppercase mb-4 block">
            {lpConfig.features.sectionLabel}
          </span>
          <h2 className="text-4xl font-bold text-[#0F1649]">
            {lpConfig.features.heading}
          </h2>
        </div>

        <div className="space-y-20">
          {features.map((feature, index) => {
            const isEven = index % 2 === 1;
            return (
              <div
                key={feature.title}
                className={cn(
                  "grid lg:grid-cols-2 gap-12 items-center",
                  index === features.length - 1 ? "mb-0" : "mb-20",
                )}
              >
                {/* Text side */}
                <div className={cn("space-y-4", isEven && "lg:order-2")}>
                  <h3 className="text-3xl font-bold text-[#0F1649] mb-4">
                    {feature.title}
                  </h3>
                  <ul className="space-y-3">
                    {feature.items.map(item => (
                      <li
                        key={item}
                        className="flex items-center gap-3 text-gray-600"
                      >
                        <CheckCircle2 className="w-5 h-5 text-[#30BF78] shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Mock UI side */}
                <div
                  className={cn(
                    "rounded-2xl bg-gray-50 border border-gray-100 p-8",
                    isEven && "lg:order-1",
                  )}
                >
                  {feature.mockContent}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
